import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen, Brain, Play, Target, Zap, GraduationCap, Award, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTY_OPTIONS = [
  { value: 'beginner', label: 'Beginner', icon: GraduationCap, color: 'bg-emerald-100 text-emerald-700 border-emerald-200', description: 'Foundation concepts' },
  { value: 'intermediate', label: 'Intermediate', icon: Zap, color: 'bg-amber-100 text-amber-700 border-amber-200', description: 'Clinical application' },
  { value: 'advanced', label: 'Advanced', icon: Award, color: 'bg-red-100 text-red-700 border-red-200', description: 'Complex reasoning' },
];

export default function QuizSetup() {
  const { topicKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [topics, setTopics] = useState([]);
  
  // Multi-select for topics (empty = all topics)
  const [selectedTopics, setSelectedTopics] = useState(topicKey ? [topicKey] : []);
  const [allTopicsSelected, setAllTopicsSelected] = useState(!topicKey);
  
  // Multi-select for difficulties (empty = all difficulties)
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [allDifficultiesSelected, setAllDifficultiesSelected] = useState(true);
  
  // Check URL params for mode (e.g., ?mode=skill-builder)
  const urlMode = searchParams.get('mode');
  const [selectedMode, setSelectedMode] = useState(urlMode === 'skill-builder' ? 'adaptive' : 'standard');
  const [questionCount, setQuestionCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingCount, setCheckingCount] = useState(false);
  
  // Multi-subtopic selection state
  const [availableSubtopics, setAvailableSubtopics] = useState([]);
  const [selectedSubtopics, setSelectedSubtopics] = useState([]); // Empty = all subtopics
  const [allSubtopicsSelected, setAllSubtopicsSelected] = useState(true);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicKey]);

  useEffect(() => {
    // When topics change, reset subtopics and fetch new ones
    if (!allTopicsSelected && selectedTopics.length > 0) {
      fetchSubtopicsForTopics(selectedTopics);
    } else {
      setAvailableSubtopics([]);
      setSelectedSubtopics([]);
      setAllSubtopicsSelected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopics, allTopicsSelected]);

  useEffect(() => {
    // Check available questions when filters change
    checkQuestionAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopics, allTopicsSelected, selectedDifficulties, allDifficultiesSelected, selectedSubtopics, allSubtopicsSelected]);

  const fetchInitialData = async () => {
    try {
      const topicsRes = await axios.get(`${BACKEND_URL}/api/topics`, { withCredentials: true });
      setTopics(topicsRes.data);

      if (topicKey) {
        const topicRes = await axios.get(`${BACKEND_URL}/api/topics/${encodeURIComponent(topicKey)}`, { withCredentials: true });
        setTopic(topicRes.data);
        setSelectedTopics([topicKey]);
        setAllTopicsSelected(false);
      }
    } catch (error) {
      if (topicKey) {
        toast.error('Topic not found');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubtopicsForTopics = async (topicKeys) => {
    try {
      // Fetch canonical subtopics for all selected topics and merge them
      const allSubtopics = [];
      const seenSlugs = new Set();
      
      for (const tk of topicKeys) {
        try {
          // Use canonical subtopics endpoint
          const response = await axios.get(`${BACKEND_URL}/api/admin/subtopics-for-topic/${encodeURIComponent(tk)}`, {
            withCredentials: true
          });
          for (const sub of (response.data || [])) {
            // Use slug as the canonical key for deduplication
            const slug = sub.slug || sub.subtopic_id || sub.key;
            if (slug && !seenSlugs.has(slug)) {
              seenSlugs.add(slug);
              allSubtopics.push({
                key: slug,
                display: sub.display_name || sub.display,
                question_count: sub.question_count || 0
              });
            }
          }
        } catch (e) {
          // Fallback: try the old endpoint
          try {
            const fallbackRes = await axios.get(`${BACKEND_URL}/api/admin/subtopics/${encodeURIComponent(tk)}`, {
              withCredentials: true
            });
            for (const sub of (fallbackRes.data || [])) {
              const slug = sub.key || sub.slug;
              if (slug && !seenSlugs.has(slug)) {
                seenSlugs.add(slug);
                allSubtopics.push({
                  key: slug,
                  display: sub.display || sub.display_name
                });
              }
            }
          } catch (e2) {
            // Final fallback: extract from topic data
            const topicData = topics.find(t => t.topic_key === tk);
            if (topicData?.subtopics) {
              for (const s of topicData.subtopics) {
                const key = s.subtopic_key || s.key;
                if (key && !seenSlugs.has(key)) {
                  seenSlugs.add(key);
                  allSubtopics.push({
                    key,
                    display: s.subtopic_display_name || s.display || s
                  });
                }
              }
            }
          }
        }
      }
      
      // Sort by display name
      allSubtopics.sort((a, b) => (a.display || '').localeCompare(b.display || ''));
      
      setAvailableSubtopics(allSubtopics);
      setSelectedSubtopics([]);
      setAllSubtopicsSelected(true);
    } catch (error) {
      setAvailableSubtopics([]);
      setSelectedSubtopics([]);
      setAllSubtopicsSelected(true);
    }
  };

  const checkQuestionAvailability = useCallback(async () => {
    setCheckingCount(true);
    try {
      const params = {
        mode: selectedMode,
        limit: 1  // Just checking availability
      };
      
      // Add topics filter if specific ones selected
      if (!allTopicsSelected && selectedTopics.length > 0) {
        params.topics = selectedTopics.join(',');
      }
      
      // Add difficulties filter if specific ones selected
      if (!allDifficultiesSelected && selectedDifficulties.length > 0) {
        params.difficulties = selectedDifficulties.join(',');
      }
      
      // Add subtopics filter if specific ones selected
      if (!allSubtopicsSelected && selectedSubtopics.length > 0) {
        params.subtopics = selectedSubtopics.join(',');
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/quiz/questions`, {
        params,
        withCredentials: true
      });
      
      setQuestionCount(response.data.meta.total_available);
    } catch (error) {
      setQuestionCount(0);
    } finally {
      setCheckingCount(false);
    }
  }, [selectedTopics, allTopicsSelected, selectedDifficulties, allDifficultiesSelected, selectedMode, selectedSubtopics, allSubtopicsSelected]);

  // Topic multi-select handlers
  const handleTopicToggle = (topicKey) => {
    if (allTopicsSelected) {
      setAllTopicsSelected(false);
      setSelectedTopics([topicKey]);
    } else {
      setSelectedTopics(prev => {
        if (prev.includes(topicKey)) {
          const newSelection = prev.filter(t => t !== topicKey);
          if (newSelection.length === 0) {
            setAllTopicsSelected(true);
          }
          return newSelection;
        } else {
          return [...prev, topicKey];
        }
      });
    }
  };

  const handleAllTopicsToggle = () => {
    setAllTopicsSelected(true);
    setSelectedTopics([]);
  };

  // Difficulty multi-select handlers
  const handleDifficultyToggle = (diffValue) => {
    if (allDifficultiesSelected) {
      setAllDifficultiesSelected(false);
      setSelectedDifficulties([diffValue]);
    } else {
      setSelectedDifficulties(prev => {
        if (prev.includes(diffValue)) {
          const newSelection = prev.filter(d => d !== diffValue);
          if (newSelection.length === 0) {
            setAllDifficultiesSelected(true);
          }
          return newSelection;
        } else {
          return [...prev, diffValue];
        }
      });
    }
  };

  const handleAllDifficultiesToggle = () => {
    setAllDifficultiesSelected(true);
    setSelectedDifficulties([]);
  };

  // Subtopic multi-select handlers
  const handleSubtopicToggle = (subtopicKey) => {
    if (allSubtopicsSelected) {
      setAllSubtopicsSelected(false);
      setSelectedSubtopics([subtopicKey]);
    } else {
      setSelectedSubtopics(prev => {
        if (prev.includes(subtopicKey)) {
          const newSelection = prev.filter(s => s !== subtopicKey);
          if (newSelection.length === 0) {
            setAllSubtopicsSelected(true);
          }
          return newSelection;
        } else {
          return [...prev, subtopicKey];
        }
      });
    }
  };

  const handleAllSubtopicsToggle = () => {
    setAllSubtopicsSelected(true);
    setSelectedSubtopics([]);
  };

  const handleStartQuiz = () => {
    const params = new URLSearchParams();
    if (!allTopicsSelected && selectedTopics.length > 0) {
      params.set('topics', selectedTopics.join(','));
    }
    if (!allDifficultiesSelected && selectedDifficulties.length > 0) {
      params.set('difficulties', selectedDifficulties.join(','));
    }
    if (!allSubtopicsSelected && selectedSubtopics.length > 0) {
      params.set('subtopics', selectedSubtopics.join(','));
    }
    params.set('mode', selectedMode);
    
    navigate(`/quiz/session?${params.toString()}`);
  };

  // Get display text for topics in summary
  const getTopicsSummaryText = () => {
    if (allTopicsSelected || selectedTopics.length === 0) {
      return 'All Topics';
    }
    if (selectedTopics.length === 1) {
      const t = topics.find(t => t.topic_key === selectedTopics[0]);
      return t?.topic_display_name || selectedTopics[0];
    }
    const firstT = topics.find(t => t.topic_key === selectedTopics[0]);
    return `${firstT?.topic_display_name || selectedTopics[0]} +${selectedTopics.length - 1}`;
  };

  // Get display text for difficulties in summary
  const getDifficultiesSummaryText = () => {
    if (allDifficultiesSelected || selectedDifficulties.length === 0) {
      return 'All Levels';
    }
    if (selectedDifficulties.length === 1) {
      const d = DIFFICULTY_OPTIONS.find(d => d.value === selectedDifficulties[0]);
      return d?.label || selectedDifficulties[0];
    }
    const firstD = DIFFICULTY_OPTIONS.find(d => d.value === selectedDifficulties[0]);
    return `${firstD?.label || selectedDifficulties[0]} +${selectedDifficulties.length - 1}`;
  };

  // Get display text for subtopics in summary
  const getSubtopicsSummaryText = () => {
    if (allSubtopicsSelected || selectedSubtopics.length === 0) {
      return 'All Subtopics';
    }
    if (selectedSubtopics.length === 1) {
      const sub = availableSubtopics.find(s => s.key === selectedSubtopics[0]);
      return sub?.display || selectedSubtopics[0];
    }
    const firstSub = availableSubtopics.find(s => s.key === selectedSubtopics[0]);
    return `${firstSub?.display || selectedSubtopics[0]} +${selectedSubtopics.length - 1}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // For backwards compatibility, get first selected topic data
  const selectedTopicData = selectedTopics.length > 0 ? topics.find(t => t.topic_key === selectedTopics[0]) : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="hover:bg-slate-100 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-sans">Study Session Setup</h1>
          <p className="text-slate-600 mt-1">Configure your quiz experience</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Mode Selection */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Mode</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setSelectedMode('standard')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                selectedMode === 'standard'
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${selectedMode === 'standard' ? 'bg-primary/10' : 'bg-slate-100'}`}>
                  <BookOpen className={`w-5 h-5 ${selectedMode === 'standard' ? 'text-primary' : 'text-slate-600'}`} />
                </div>
                <h3 className="font-semibold text-slate-900">Standard Quiz</h3>
              </div>
              <p className="text-sm text-slate-600">
                Practice questions from your selected topic and difficulty. Great for focused study sessions.
              </p>
            </button>

            <button
              onClick={() => setSelectedMode('skill_builder')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                selectedMode === 'skill_builder'
                  ? 'border-accent bg-accent/5 shadow-md'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${selectedMode === 'skill_builder' ? 'bg-accent/10' : 'bg-slate-100'}`}>
                  <Brain className={`w-5 h-5 ${selectedMode === 'skill_builder' ? 'text-accent' : 'text-slate-600'}`} />
                </div>
                <h3 className="font-semibold text-slate-900">Skill Builder</h3>
              </div>
              <p className="text-sm text-slate-600">
                Adaptive mode that focuses on your weak areas. Improves long-term retention.
              </p>
            </button>
          </div>
        </div>

        {/* Topic Selection - Multi-select */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Topics</h2>
          <p className="text-sm text-slate-500 mb-3">Choose one or more topics, or select all</p>
          <div className="flex flex-wrap gap-2">
            {/* All Topics chip */}
            <button
              onClick={handleAllTopicsToggle}
              className={`px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2 ${
                allTopicsSelected
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-slate-200 hover:border-slate-300 text-slate-600'
              }`}
            >
              {allTopicsSelected && <Check className="w-4 h-4" />}
              <span className="font-medium text-sm">All Topics</span>
            </button>
            
            {topics.map((t) => {
              const isSelected = !allTopicsSelected && selectedTopics.includes(t.topic_key);
              return (
                <button
                  key={t.topic_key}
                  onClick={() => handleTopicToggle(t.topic_key)}
                  className={`px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2 ${
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-slate-200 hover:border-slate-300 text-slate-600'
                  }`}
                >
                  {isSelected && <Check className="w-4 h-4" />}
                  <span className="font-medium text-sm">{t.topic_display_name}</span>
                  <span className="text-xs text-slate-400">({t.question_count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Subtopic Selection - Only shows when specific topics are selected */}
        {!allTopicsSelected && selectedTopics.length > 0 && availableSubtopics.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Subtopics</h2>
            <p className="text-sm text-slate-500 mb-3">Choose specific subtopics or select all</p>
            <div className="flex flex-wrap gap-2">
              {/* All Subtopics chip */}
              <button
                onClick={handleAllSubtopicsToggle}
                className={`px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2 ${
                  allSubtopicsSelected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-slate-200 hover:border-slate-300 text-slate-600'
                }`}
              >
                {allSubtopicsSelected && <Check className="w-4 h-4" />}
                <span className="font-medium text-sm">All Subtopics</span>
              </button>
              
              {/* Individual subtopic chips */}
              {availableSubtopics.map((subtopic) => {
                const isSelected = !allSubtopicsSelected && selectedSubtopics.includes(subtopic.key);
                return (
                  <button
                    key={subtopic.key}
                    onClick={() => handleSubtopicToggle(subtopic.key)}
                    className={`px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2 ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                  >
                    {isSelected && <Check className="w-4 h-4" />}
                    <span className="font-medium text-sm">{subtopic.display}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Difficulty Selection - Multi-select */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Difficulty</h2>
          <p className="text-sm text-slate-500 mb-3">Choose one or more difficulty levels</p>
          <div className="flex flex-wrap gap-2">
            {/* All Levels chip */}
            <button
              onClick={handleAllDifficultiesToggle}
              className={`px-4 py-3 rounded-full border-2 transition-all flex items-center gap-2 ${
                allDifficultiesSelected
                  ? 'border-primary bg-slate-100 text-slate-700'
                  : 'border-slate-200 hover:border-slate-300 text-slate-600'
              }`}
            >
              {allDifficultiesSelected && <Check className="w-4 h-4" />}
              <Target className="w-4 h-4" />
              <span className="font-medium text-sm">All Levels</span>
            </button>
            
            {DIFFICULTY_OPTIONS.map((diff) => {
              const Icon = diff.icon;
              const isSelected = !allDifficultiesSelected && selectedDifficulties.includes(diff.value);
              return (
                <button
                  key={diff.value}
                  onClick={() => handleDifficultyToggle(diff.value)}
                  className={`px-4 py-3 rounded-full border-2 transition-all flex items-center gap-2 ${
                    isSelected
                      ? `border-primary ${diff.color}`
                      : 'border-slate-200 hover:border-slate-300 bg-white text-slate-600'
                  }`}
                >
                  {isSelected && <Check className="w-4 h-4" />}
                  <Icon className={`w-4 h-4 ${isSelected ? '' : 'text-slate-400'}`} />
                  <span className="font-medium text-sm">{diff.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Summary & Start */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Quiz Summary</h3>
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedMode === 'skill_builder' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'
                }`}>
                  {selectedMode === 'skill_builder' ? 'Skill Builder' : 'Standard Quiz'}
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {getTopicsSummaryText()}
                </span>
                {/* Subtopics pill - only show if topics are selected and not "All Subtopics" */}
                {!allTopicsSelected && selectedTopics.length > 0 && (
                  <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">
                    {getSubtopicsSummaryText()}
                  </span>
                )}
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                  {getDifficultiesSummaryText()}
                </span>
              </div>
              <div className="mt-2 text-sm text-slate-500">
                {checkingCount ? (
                  <span>Checking available questions...</span>
                ) : questionCount !== null ? (
                  <span>{questionCount} questions available</span>
                ) : null}
              </div>
            </div>

            <Button
              onClick={handleStartQuiz}
              disabled={questionCount === 0}
              className={`h-14 px-8 rounded-full font-semibold text-lg shadow-lg transition-all hover:-translate-y-0.5 ${
                selectedMode === 'skill_builder'
                  ? 'bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent/20'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/20'
              }`}
            >
              <Play className="w-5 h-5 mr-2" />
              {questionCount === 0 ? 'No Questions Available' : 'Start Quiz'}
            </Button>
          </div>

          {questionCount === 0 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                No questions match your selected filters. Try selecting &quot;All Topics&quot; or &quot;All Levels&quot; to find available questions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
